import React, { useState, useEffect, useMemo } from 'react';
import Modal from 'react-modal';
import { StyledPaper, StyledTable, StyledTableRow, StyledTableCell } from '../../../uiComponents/MuiTable/StyledTable.js'
import MuiTableHeader from '../../../uiComponents/MuiTable/MuiTableHeader.js';
import {stableSort,getComparator} from '../../../uiComponents/MuiTable/MuiHelper.js';
import { 
  TableBody,
  TableContainer,
  Grid,
} from '@mui/material';

const headCells = [
  { label: 'Step', id: 'step', numeric: false, disablePadding: false },
  { label: 'Description', id: 'description', numeric: false, disablePadding: false },
  { label: 'Status', id: 'status', numeric: false, disablePadding: false },
];

const failedHeadCells = [
  { label: 'Step', id: 'step', numeric: false, disablePadding: false },
  { label: 'Description', id: 'description', numeric: false, disablePadding: false },
  { label: 'Error Message', id: 'message', numeric: false, disablePadding: false },
];

const csvInstrumentationHeadCells = [
  { label: 'Analyte Group', id: 'AnalyteGroup', numeric: false, disablePadding: false },
  { label: 'Instrument', id: 'Instrument', numeric: false, disablePadding: false },
  { label: 'Reagent Kit', id: 'ReagentKit', numeric: false, disablePadding: false },
  { label: 'Lot Number', id: 'LotNumber', numeric: false, disablePadding: false },
];

const vcfInputParameterHeadCells = [
  { label: 'Input Parameter', id: 'parameter', numeric: false, disablePadding: false, type: 'noSort' },
  { label: 'Value', id: 'value', numeric: false, disablePadding: false, type: 'noSort' },
];

const vcfReviewSettingsHeadCells = [
  { label: 'Variant', id: 'variant', numeric: false, disablePadding: false, type: 'noSort' },
  { label: 'Create Review?', id: 'createReview', numeric: false, disablePadding: false, type: 'noSort' },
];

// Helper to format camelCase/PascalCase keys to readable labels
const formatParameterLabel = (key) => {
  // Insert space before capital letters and capitalize first letter
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
};

export default function PipelineStepsModal(props) {
  const { modalIsOpen, closeModal, pipelineSteps } = props;
  // Memoize steps, workflowName, failedSteps to avoid dependency changes
  const steps = useMemo(() => pipelineSteps?.steps || [], [pipelineSteps]);
  const workflowName = useMemo(() => pipelineSteps?.name || '', [pipelineSteps]);
  const input = useMemo(() => pipelineSteps?.input || {}, [pipelineSteps]);
  const analyteGroups = useMemo(() => pipelineSteps?.analyteGroupEnum || {}, [pipelineSteps]);
  // Determine if this is a CSV response (has Instrumentation array)
  const isCsvResponse = useMemo(() => {
    return Array.isArray(input?.Instrumentation);
  }, [input]);

  // Determine if this is a Clinical CSV response (steps contain "Clinical" in description or message)
  const isClinicalCsvResponse = useMemo(() => {
    if (isCsvResponse) return false;
    return steps.some(step =>
      (step.description && step.description.toLowerCase().includes('clinical')) ||
      (step.message && step.message.toLowerCase().includes('clinical'))
    );
  }, [steps, isCsvResponse]);

  // CSV instrumentation data
  const instrumentationData = useMemo(() => {
    return input?.Instrumentation || [];
  }, [input]);

  // Input parameters (non-array items) - used for both VCF and Clinical CSV
  const vcfInputParameters = useMemo(() => {
    if (isCsvResponse) return [];
    return Object.entries(input)
      .filter(([key, value]) => !Array.isArray(value))
      .map(([key, value]) => ({
        parameter: formatParameterLabel(key),
        value: value !== null ? String(value) : ''
      }));
  }, [input, isCsvResponse]);

  // VCF review settings
  const vcfReviewSettings = useMemo(() => {
    if (isCsvResponse || isClinicalCsvResponse) return [];
    const reviewSettings = input?.ReviewSettings || [];
    return reviewSettings.map(setting => ({
      variant: `${setting.Chrom}-${setting.Pos}-${setting.Ref}-${setting.Alt}`,
      createReview: setting.CreateReview ? 'Yes' : 'No'
    }));
  }, [input, isCsvResponse, isClinicalCsvResponse]);

  // Calculate empty rows needed to balance the two VCF tables
  const emptyRowsForInputParams = useMemo(() => {
    const diff = vcfReviewSettings.length - vcfInputParameters.length;
    return diff > 0 ? Array(diff).fill({ parameter: '', value: '' }) : [];
  }, [vcfInputParameters.length, vcfReviewSettings.length]);

  const emptyRowsForReviewSettings = useMemo(() => {
    const diff = vcfInputParameters.length - vcfReviewSettings.length;
    return diff > 0 ? Array(diff).fill({ variant: '', createReview: '' }) : [];
  }, [vcfInputParameters.length, vcfReviewSettings.length]);

  const failedSteps = useMemo(() => {
    return steps
      .filter(step => step.status === "Failed") // Filter for steps with status "Failed"
      .map(step => ({
        step: step.step,
        description: step.description,
        message: step.message
      })); // Extract the required properties
  }, [steps]);
  const [workflowRunName, setWorkflowRunName] = useState('');
  const [tableData, setTableData] = useState([]);
  const [failedTableData, setFailedTableData] = useState([]);
  const [order, setOrder] = useState('asc'); //default sort direction
  const [orderBy, setOrderBy] = useState('step'); //default sort column for table

  useEffect(() => {
    if (modalIsOpen) {
      setWorkflowRunName(workflowName);
      setTableData(steps);
      setFailedTableData(failedSteps);
    }
  }, [modalIsOpen, steps, workflowName, failedSteps]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <>
      <Modal
        className="modal pipelineStepsModal"
        overlayClassName="modalOverlay"
        aria-modal="true"
        contentLabel="Pipeline Steps"
        shouldCloseOnOverlayClick={true}
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
      >
        <div className="modalHeader">Pipeline: {workflowRunName}</div>
        <div className="modalContent">
          {/* Inputs Section */}
          <div className="modalSection">
            <div className="sectionHeading center">
              Inputs
            </div>
            {isCsvResponse ? (
              /* CSV Response - Single Instrumentation Table */
              <StyledPaper>
                <TableContainer>
                  <StyledTable
                    size='small'
                    aria-label="instrumentation inputs"
                  >
                    <MuiTableHeader
                      headCells={csvInstrumentationHeadCells}
                      order={order}
                      orderBy={orderBy}
                      onRequestSort={handleRequestSort}
                    />
                    <TableBody>
                      {instrumentationData.length ? (
                        stableSort(instrumentationData, getComparator(order, orderBy))
                          .map((row, index) => (
                            <StyledTableRow tabIndex={-1} key={index}>
                              <StyledTableCell>{analyteGroups[row.AnalyteGroup]}</StyledTableCell>
                              <StyledTableCell>{row.Instrument}</StyledTableCell>
                              <StyledTableCell>{row.ReagentKit}</StyledTableCell>
                              <StyledTableCell>{row.LotNumber}</StyledTableCell>
                            </StyledTableRow>
                          ))
                      ) : (
                        <StyledTableRow>
                          <StyledTableCell style={{textAlign: 'center'}} colSpan={4}>No Instrumentation Data Found</StyledTableCell>
                        </StyledTableRow>
                      )}
                    </TableBody>
                  </StyledTable>
                </TableContainer>
              </StyledPaper>
            ) : isClinicalCsvResponse ? (
              /* Clinical CSV Response - Full Width Input Parameters Table */
              <StyledPaper>
                <TableContainer>
                  <StyledTable
                    size='small'
                    aria-label="clinical input parameters"
                    style={{ tableLayout: 'fixed', width: '100%', minWidth: 'unset' }}
                  >
                    <colgroup>
                      <col style={{ width: '40%' }} />
                      <col style={{ width: '60%' }} />
                    </colgroup>
                    <MuiTableHeader
                      headCells={vcfInputParameterHeadCells}
                      order={order}
                      orderBy={orderBy}
                      onRequestSort={handleRequestSort}
                    />
                    <TableBody>
                      {vcfInputParameters.length ? (
                        vcfInputParameters.map((row, index) => (
                          <StyledTableRow tabIndex={-1} key={index}>
                            <StyledTableCell>{row.parameter}</StyledTableCell>
                            <StyledTableCell style={{ wordBreak: 'break-all', overflowWrap: 'break-word' }}>{row.value}</StyledTableCell>
                          </StyledTableRow>
                        ))
                      ) : (
                        <StyledTableRow>
                          <StyledTableCell style={{textAlign: 'center'}} colSpan={2}>No Input Parameters Found</StyledTableCell>
                        </StyledTableRow>
                      )}
                    </TableBody>
                  </StyledTable>
                </TableContainer>
              </StyledPaper>
            ) : (
              /* VCF Response - Two Tables Side by Side */
              <Grid container spacing={2} style={{ alignItems: 'stretch' }}>
                {/* Input Parameters Table */}
                <Grid item xs={6} style={{ display: 'flex' }}>
                  <StyledPaper style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <TableContainer style={{ flex: 1 }}>
                      <StyledTable
                        size='small'
                        aria-label="input parameters"
                        style={{ tableLayout: 'fixed', width: '100%', minWidth: 'unset' }}
                      >
                        <colgroup>
                          <col style={{ width: '40%' }} />
                          <col style={{ width: '60%' }} />
                        </colgroup>
                        <MuiTableHeader
                          headCells={vcfInputParameterHeadCells}
                          order={order}
                          orderBy={orderBy}
                          onRequestSort={handleRequestSort}
                        />
                        <TableBody>
                          {vcfInputParameters.length ? (
                            <>
                              {vcfInputParameters.map((row, index) => (
                                <StyledTableRow tabIndex={-1} key={index}>
                                  <StyledTableCell>{row.parameter}</StyledTableCell>
                                  <StyledTableCell style={{ wordBreak: 'break-all', overflowWrap: 'break-word' }}>{row.value}</StyledTableCell>
                                </StyledTableRow>
                              ))}
                              {emptyRowsForInputParams.map((_, index) => (
                                <StyledTableRow tabIndex={-1} key={`empty-${index}`}>
                                  <StyledTableCell>&nbsp;</StyledTableCell>
                                  <StyledTableCell>&nbsp;</StyledTableCell>
                                </StyledTableRow>
                              ))}
                            </>
                          ) : (
                            <StyledTableRow>
                              <StyledTableCell style={{textAlign: 'center'}} colSpan={2}>No Input Parameters Found</StyledTableCell>
                            </StyledTableRow>
                          )}
                        </TableBody>
                      </StyledTable>
                    </TableContainer>
                  </StyledPaper>
                </Grid>
                {/* Review Settings Table */}
                <Grid item xs={6} style={{ display: 'flex' }}>
                  <StyledPaper style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <TableContainer style={{ flex: 1 }}>
                      <StyledTable
                        size='small'
                        aria-label="review settings"
                        style={{ tableLayout: 'fixed', width: '100%', minWidth: 'unset' }}
                      >
                        <MuiTableHeader
                          headCells={vcfReviewSettingsHeadCells}
                          order={order}
                          orderBy={orderBy}
                          onRequestSort={handleRequestSort}
                        />
                        <TableBody>
                          {vcfReviewSettings.length ? (
                            <>
                              {vcfReviewSettings.map((row, index) => (
                                <StyledTableRow tabIndex={-1} key={index}>
                                  <StyledTableCell style={{ width: '50%' }}>{row.variant}</StyledTableCell>
                                  <StyledTableCell style={{ width: '50%' }}>{row.createReview}</StyledTableCell>
                                </StyledTableRow>
                              ))}
                              {emptyRowsForReviewSettings.map((_, index) => (
                                <StyledTableRow tabIndex={-1} key={`empty-${index}`}>
                                  <StyledTableCell>&nbsp;</StyledTableCell>
                                  <StyledTableCell>&nbsp;</StyledTableCell>
                                </StyledTableRow>
                              ))}
                            </>
                          ) : (
                            <StyledTableRow>
                              <StyledTableCell style={{textAlign: 'center'}} colSpan={2}>No Review Settings Found</StyledTableCell>
                            </StyledTableRow>
                          )}
                        </TableBody>
                      </StyledTable>
                    </TableContainer>
                  </StyledPaper>
                </Grid>
              </Grid>
            )}
          </div>
          {/* Steps Section */}
          <div className="modalSection">
            <div className="sectionHeading center">
              Steps
            </div>
            <StyledPaper>
              <TableContainer>
                <StyledTable
                  size='small'
                  aria-label="pipeline steps" 
                >
                  <MuiTableHeader 
                    headCells={headCells} 
                    order={order}  
                    orderBy={orderBy} 
                    onRequestSort={handleRequestSort}
                  />
                  <TableBody>
                  { tableData.length ? (
                    stableSort(tableData, getComparator(order, orderBy))
                      .map((row, index) => {
                        return (
                          <StyledTableRow tabIndex={-1} key={index}>
                            <StyledTableCell>{row.step}</StyledTableCell>
                            <StyledTableCell>{row.description}</StyledTableCell>
                            <StyledTableCell>{row.status}</StyledTableCell>
                          </StyledTableRow>
                        );
                      })
                    ) : (
                      <StyledTableRow>
                        <StyledTableCell style={{textAlign: 'center'}} colSpan={12}>No Pipeline Data Found</StyledTableCell>
                      </StyledTableRow>
                    )
                  }
                  </TableBody>
                </StyledTable>
              </TableContainer>
            </StyledPaper>
          </div>
          { failedTableData.length ? (
            <div className="modalSection">
              <div className="sectionHeading center">
                Errors
              </div>
              <StyledPaper>
                <TableContainer>
                  <StyledTable
                    size='small'
                    aria-label="pipeline steps" 
                  >
                    <MuiTableHeader 
                      headCells={failedHeadCells} 
                      order={order}  
                      orderBy={orderBy} 
                      onRequestSort={handleRequestSort}
                    />
                    <TableBody>
                    {stableSort(failedTableData, getComparator(order, orderBy))
                      .map((row, index) => {
                        return (
                          <StyledTableRow tabIndex={-1} key={index}>
                            <StyledTableCell>{row.step}</StyledTableCell>
                            <StyledTableCell>{row.description}</StyledTableCell>
                            <StyledTableCell>{row.message}</StyledTableCell>
                          </StyledTableRow>
                        );
                      })
                    }
                    </TableBody>
                  </StyledTable>
                </TableContainer>
              </StyledPaper>
            </div>
            ) : ('')
          }
        </div>
        <div className="modalButtons">
          <button
            onClick={closeModal}
            className="modalButton"
            variant="contained"
            aria-label="close pipeline steps"
          >
            Close
          </button>
        </div>
      </Modal>
    </>
  );
}
